import { CacheFunction } from "@pdfking/compatibility"
import type {
  Degree,
  Distance,
  Page,
  Pixel,
  PlotMedia,
  PlotPath,
  PlotRect,
  PlotText,
  PlottingContext,
  Point,
  Rectangle,
  Scale,
  SpatialRectangle,
} from "@pdfking/renderer-api"
import {
  ComputeScale,
  CreateContext,
  HorizontalAlign,
  PlotFunction,
  Rotate,
  VerticalAlign,
} from "@pdfking/renderer-api"
import { BlobToUint8, Identity, ToBlob } from "@pdfking/tfunc"
import type {
  PageBoundingBox,
  PDFEmbeddedPage,
  PDFFont,
  PDFPage,
} from "pdf-lib"
import type { CMYK, RGB } from "pdf-lib"
import {
  closePath,
  cmyk,
  degrees,
  fill,
  fillAndStroke,
  lineTo,
  moveTo,
  PDFDocument,
  popGraphicsState,
  pushGraphicsState,
  setFillingColor,
  setGraphicsState,
  setLineWidth,
  setStrokingColor,
  StandardFonts, // revert to 180a53d068 to restore custom font
  stroke,
} from "pdf-lib"

import { BytesToPdf } from "../utils/BytesToPdf"
import { ReplaceFontCharacters } from "../utils/ReplaceFontCharacters"
import { ToPdflibColor } from "../utils/ToPdflibColor"

/**
 * Type representing the context used in the current implementation
 * of the PlotPage function. This combines the intended PlottingContext
 * type with all additional properties actually used in the code.
 */

type ColorAlpha = {
  color: CMYK | RGB | undefined
  opacity: number
}

type PageBlob = {
  page: PDFPage
  scale: Scale
}

type CurrentPlottingContext = {
  // PDF-lib related objects
  doc: PDFDocument
  page: PDFPage
  font: PDFFont

  scale_bleed: Pixel
  stroke_width: Pixel

  // Coordinate transformation methods
  get_xy: (point: Point<Distance>) => Point<Pixel>
  get_measures: (
    position: SpatialRectangle<Distance>
  ) => SpatialRectangle<Pixel>
  get_color: (id: string) => ColorAlpha

  // PDF page retrieval methods
  get_page: (blob_id: string) => Promise<PageBlob | undefined>
  get_embedded_page: (
    page: PDFPage,
    mask: PageBoundingBox
  ) => Promise<PDFEmbeddedPage | undefined>
}

const white = cmyk(0, 0, 0, 0) // undefined // can be white instead but it is better to avoid transparency

const todegree = (angle: Degree) => degrees(-(angle || 0))

const CreateCurrentPlottingContext = async (
  context: Required<PlottingContext>,
  drawing: Page
): Promise<CurrentPlottingContext> => {
  const to_px = (value: Distance): Pixel =>
    (context.unit_to_px * value) as Pixel

  const get_size = ({
    width,
    height,
  }: Rectangle<Distance>): Rectangle<Pixel> => ({
    width: to_px(width) as Pixel,
    height: to_px(height) as Pixel,
  })

  const get_xy = ({ x: px, y: py }: Point<Distance>): Point<Pixel> => ({
    x: (bleed + to_px(px)) as Pixel,
    y: (bleed - to_px(py) + height) as Pixel,
  })

  const get_measures = (
    r: SpatialRectangle<Distance>
  ): SpatialRectangle<Pixel> => {
    const xy = get_xy(r)
    const size = get_size(r)

    return {
      x: xy.x,
      y: (xy.y - size.height) as Pixel,
      ...size,
    }
  }

  const { width, height } = get_size(drawing)
  const bleed = to_px(drawing.bleed ?? context.page_bleed)
  const rotation = todegree(0 as Degree)

  const get_color = (id: string): ColorAlpha => {
    const color = context.color_map[id]
    return color
      ? { opacity: color.a, color: ToPdflibColor(color) }
      : { opacity: 0, color: undefined }
  }

  const get_page = async (blob_id: string): Promise<PageBlob | undefined> => {
    const promise = context.file_map[blob_id]
    if (promise) {
      const blob = await promise
      const doc = await BytesToPdf(
        await BlobToUint8(blob),
        blob.type,
        context.image_resolution
      )
      if (doc.getPageCount() == 1) {
        return { scale: context.scale_map[blob_id], page: doc.getPage(0) }
      }
      throw new Error("PDF must have exactly one page")
    }
    return undefined
  }

  const get_embedded_page = async (
    page: PDFPage,
    mask: PageBoundingBox
  ): Promise<PDFEmbeddedPage | undefined> => {
    // this is done becose an empty page raises an errors
    const { Contents } = page
      ? page.node.normalizedEntries()
      : { Contents: undefined }

    return Contents ? await doc.embedPage(page, mask) : undefined
  }

  const doc = await PDFDocument.create()

  const font = await doc.embedFont(StandardFonts.Helvetica)

  const page = doc.addPage([width + bleed * 2, height + bleed * 2])
  page.setBleedBox(0, 0, width + bleed * 2, height + bleed * 2)
  page.setTrimBox(bleed, bleed, width, height)
  page.setMediaBox(0, 0, width + bleed * 2, height + bleed * 2)
  page.setRotation(rotation)
  page.setFont(font)

  return {
    doc,
    font,
    page,
    get_xy,
    get_page: CacheFunction(get_page, Identity),
    get_measures,
    get_embedded_page,
    get_color,
    stroke_width: to_px(context.stroke_width),
    scale_bleed: to_px(context.scale_bleed),
  }
}

const plot_text = async (
  context: CurrentPlottingContext,
  {
    stroke_color,
    fill_color,
    position,
    rotation = 0 as Degree,
    text: original,
    font_size = 12,
    text_color,
    horizontal_align,
    vertical_align,
  }: PlotText
): Promise<void> => {
  if (stroke_color || fill_color) {
    plot_rect(context, {
      func: PlotFunction.PLOT_RECT,
      rotation: 0 as Degree,
      stroke_color,
      fill_color,
      position,
    })
  }

  const text = ReplaceFontCharacters(original, context.font)

  const fw = context.font.widthOfTextAtSize(text, font_size)
  // Use height without descender for more accurate vertical positioning
  const fh = context.font.heightAtSize(font_size, { descender: false })

  const { x, y, width, height } = context.get_measures(position)

  const { x: w, y: h } = Rotate(0 as Pixel, 0 as Pixel, width, height, rotation)

  let ew = 0
  let eh = 0

  if (horizontal_align == HorizontalAlign.LEFT) {
    ew = -(Math.abs(w) - fw) / 2
  } else if (horizontal_align == HorizontalAlign.RIGHT) {
    ew = +(Math.abs(w) - fw) / 2
  }
  if (vertical_align == VerticalAlign.TOP) {
    eh = +(Math.abs(h) - fh) / 2
  } else if (vertical_align == VerticalAlign.BOTTOM) {
    eh = -(Math.abs(h) - fh) / 2
  }

  const coord = Rotate(
    (x + width / 2) as Pixel,
    (y + height / 2) as Pixel,
    (x + width / 2 - fw / 2 + ew) as Pixel,
    (y + height / 2 - fh / 2 + eh) as Pixel,
    rotation
  )

  context.page.drawText(text, {
    ...coord,
    ...context.get_color(text_color),
    size: font_size,
    rotate: todegree(rotation),
    font: context.font,
  })
}

const plot_rect = async (
  context: CurrentPlottingContext,
  { fill_color, stroke_color, position, rotation = 0 as Degree }: PlotRect
): Promise<void> => {
  const { color, opacity } = context.get_color(fill_color ?? "")
  const { color: borderColor, opacity: borderOpacity } = context.get_color(
    stroke_color ?? ""
  )

  const { x, y, width, height } = context.get_measures(position)

  const coord = Rotate(
    (x + width / 2) as Pixel,
    (y + height / 2) as Pixel,
    x,
    y,
    rotation
  )

  context.page.drawRectangle({
    ...coord,
    width,
    height,
    rotate: todegree(rotation),
    borderWidth: context.stroke_width,
    borderColor,
    borderOpacity,
    color,
    opacity,
  })
}

const plot_media = async (
  context: CurrentPlottingContext,
  { page, crop_position, media_position, rotation = 0 as Degree }: PlotMedia
): Promise<void> => {
  const spec = await context.get_page(page)

  if (!spec) {
    return
  }

  context.page.drawRectangle({
    ...context.get_measures(crop_position),
    color: white,
  })

  const inner = spec.page.getTrimBox() as SpatialRectangle<Pixel>

  const media = context.get_measures(media_position)
  const crop = context.get_measures(crop_position)

  const { x: scale_x, y: scale_y } = ComputeScale<Pixel>(
    { outer: media, inner, bleed: context.scale_bleed },
    spec.scale || undefined
  )

  const { x: tx, y: ty, width: tw, height: th } = inner
  const { x: mx, y: my, width: mw, height: mh } = media
  const { x: cx, y: cy, width: cw, height: ch } = crop

  const center_x = (tx + tw / 2) as Pixel
  const center_y = (ty + th / 2) as Pixel

  const { x: left, y: bottom } = Rotate(
    center_x,
    center_y,
    (center_x - (mx + mw / 2 - cx) / scale_x) as Pixel,
    (center_y - (my + mh / 2 - cy) / scale_y) as Pixel,
    -rotation as Degree
  )
  const { x: right, y: top } = Rotate(
    center_x,
    center_y,
    (center_x + (mw / 2 + (cx + cw - (mx + mw))) / scale_x) as Pixel,
    (center_y + (mh / 2 + (cy + ch - (my + mh))) / scale_y) as Pixel,
    -rotation as Degree
  )

  const mask = { top, left, right, bottom }

  const embedded = await context.get_embedded_page(spec.page, mask)

  await context.doc.flush()

  if (embedded)
    context.page.drawPage(embedded, {
      rotate: todegree(rotation),
      x: cx,
      y: cy,
      xScale: scale_x,
      yScale: scale_y,
    })
}

const plot_path = async (
  context: CurrentPlottingContext,
  { stroke_color, fill_color, points, closed }: PlotPath
): Promise<void> => {
  const { color: strokeCol, opacity: strokeOpacity } = context.get_color(
    stroke_color ?? ""
  )
  const { color: fillCol, opacity: fillOpacity } = context.get_color(
    fill_color ?? ""
  )
  const [{ x, y }, ...rest] = points.map(context.get_xy)

  const hasStroke = !!strokeCol
  const hasFill = !!fillCol && closed

  const paintOp =
    hasFill && hasStroke ? fillAndStroke() : hasFill ? fill() : stroke()

  // Create graphics state for opacity if needed
  let graphicsStateKey:
    | ReturnType<typeof context.page.node.newExtGState>
    | undefined
  if (
    (hasFill && fillOpacity !== undefined && fillOpacity < 1) ||
    (hasStroke && strokeOpacity !== undefined && strokeOpacity < 1)
  ) {
    const graphicsState = context.doc.context.obj({
      Type: "ExtGState",
      ca: hasFill ? fillOpacity : undefined,
      CA: hasStroke ? strokeOpacity : undefined,
    })
    graphicsStateKey = context.page.node.newExtGState("GS", graphicsState)
  }

  context.page.pushOperators(
    pushGraphicsState(),
    ...(graphicsStateKey ? [setGraphicsState(graphicsStateKey)] : []),
    setLineWidth(context.stroke_width),
    ...(hasStroke ? [setStrokingColor(strokeCol)] : []),
    ...(hasFill ? [setFillingColor(fillCol)] : []),
    moveTo(x, y),
    ...rest.map(({ x, y }) => lineTo(x, y)),
    ...(closed ? [closePath()] : []),
    paintOp,
    moveTo(0, 0),
    popGraphicsState()
  )
}

const handlers = {
  [PlotFunction.PLOT_TEXT]: plot_text,
  [PlotFunction.PLOT_RECT]: plot_rect,
  [PlotFunction.PLOT_MEDIA]: plot_media,
  [PlotFunction.PLOT_PATH]: plot_path,
} as const

export const PlotPage = async (
  page: Page,
  partial: PlottingContext = {}
): Promise<Blob> => {
  const context = await CreateCurrentPlottingContext(
    CreateContext(partial),
    page
  )

  for (const shape of page.shapes) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await (handlers[shape.func] as any)(context, shape)
  }

  return context.doc.save().then(bytes => ToBlob(bytes, "application/pdf"))
}
